import type { AudioManagerPort, AudioPreferences } from '../services/audio';
import { DEFAULT_AUDIO_PREFERENCES, saveAudioPreferences } from '../services/audio';

function requireElement<T extends Element>(root: ParentNode, selector: string): T {
  const element = root.querySelector<T>(selector);
  if (element === null) throw new Error(`Audio-Element fehlt: ${selector}`);
  return element;
}

export class AudioSettingsController {
  private readonly enabled: HTMLButtonElement;
  private readonly master: HTMLInputElement;
  private readonly music: HTMLInputElement;
  private readonly sfx: HTMLInputElement;
  private readonly masterOutput: HTMLOutputElement;
  private readonly musicOutput: HTMLOutputElement;
  private readonly sfxOutput: HTMLOutputElement;
  private readonly mobileMusic: HTMLButtonElement;
  private readonly mobileSfx: HTMLButtonElement;
  private lastMusicVolume: number;
  private lastSfxVolume: number;

  constructor(
    root: Document,
    private readonly audio: AudioManagerPort,
    private readonly storage?: Storage,
  ) {
    this.enabled = requireElement(root, '[data-audio-enabled]');
    this.master = requireElement(root, '[data-master-volume]');
    this.music = requireElement(root, '[data-music-volume]');
    this.sfx = requireElement(root, '[data-sfx-volume]');
    this.masterOutput = requireElement(root, '[data-master-output]');
    this.musicOutput = requireElement(root, '[data-music-output]');
    this.sfxOutput = requireElement(root, '[data-sfx-output]');
    this.mobileMusic = requireElement(root, '[data-mobile-music-toggle]');
    this.mobileSfx = requireElement(root, '[data-mobile-sfx-toggle]');
    this.lastMusicVolume = audio.preferences.musicVolume > 0
      ? audio.preferences.musicVolume : DEFAULT_AUDIO_PREFERENCES.musicVolume;
    this.lastSfxVolume = audio.preferences.sfxVolume > 0
      ? audio.preferences.sfxVolume : DEFAULT_AUDIO_PREFERENCES.sfxVolume;
    this.render(audio.preferences);
  }

  bind(): () => void {
    const toggle = (): void => this.commit({ enabled: !this.audio.preferences.enabled });
    const master = (): void => this.commit({ masterVolume: Number(this.master.value) });
    const music = (): void => {
      const volume = Number(this.music.value);
      if (volume > 0) this.lastMusicVolume = volume;
      this.commit({ musicVolume: volume });
    };
    const sfx = (): void => {
      const volume = Number(this.sfx.value);
      if (volume > 0) this.lastSfxVolume = volume;
      this.commit({ sfxVolume: volume });
    };
    const mobileMusic = (): void => this.commit({
      musicVolume: this.audio.preferences.musicVolume > 0 ? 0 : this.lastMusicVolume,
    });
    const mobileSfx = (): void => this.commit({
      sfxVolume: this.audio.preferences.sfxVolume > 0 ? 0 : this.lastSfxVolume,
    });
    this.enabled.addEventListener('click', toggle);
    this.master.addEventListener('input', master);
    this.music.addEventListener('input', music);
    this.sfx.addEventListener('input', sfx);
    this.mobileMusic.addEventListener('click', mobileMusic);
    this.mobileSfx.addEventListener('click', mobileSfx);
    return () => {
      this.enabled.removeEventListener('click', toggle);
      this.master.removeEventListener('input', master);
      this.music.removeEventListener('input', music);
      this.sfx.removeEventListener('input', sfx);
      this.mobileMusic.removeEventListener('click', mobileMusic);
      this.mobileSfx.removeEventListener('click', mobileSfx);
    };
  }

  render(preferences: AudioPreferences): void {
    this.enabled.setAttribute('aria-pressed', String(preferences.enabled));
    this.enabled.querySelector('b')!.textContent = preferences.enabled ? 'An' : 'Aus';
    this.master.value = String(preferences.masterVolume);
    this.music.value = String(preferences.musicVolume);
    this.sfx.value = String(preferences.sfxVolume);
    this.masterOutput.value = `${preferences.masterVolume} %`;
    this.musicOutput.value = `${preferences.musicVolume} %`;
    this.sfxOutput.value = `${preferences.sfxVolume} %`;
    this.mobileMusic.setAttribute('aria-pressed', String(preferences.musicVolume > 0));
    this.mobileMusic.querySelector('b')!.textContent = preferences.musicVolume > 0 ? 'An' : 'Aus';
    this.mobileSfx.setAttribute('aria-pressed', String(preferences.sfxVolume > 0));
    this.mobileSfx.querySelector('b')!.textContent = preferences.sfxVolume > 0 ? 'An' : 'Aus';
  }

  private commit(patch: Partial<Omit<AudioPreferences, 'version'>>): void {
    const next: AudioPreferences = { ...this.audio.preferences, ...patch, version: 2 };
    this.audio.setPreferences(next);
    if (this.storage !== undefined) saveAudioPreferences(this.storage, next);
    this.audio.playCue('button');
    this.render(next);
  }
}
